import React, { useState } from 'react';
import { fetchNearbyRestaurants } from '../placesService';
import { createSession } from '../sessionService';
import { CuisineFilter } from '../types';

interface HomeScreenProps {
  onSessionCreated: (sessionCode: string, userName: string, user: 'userA') => void;
  onJoinSession: () => void;
}

const CUISINES: { label: string; value: CuisineFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'American', value: 'american' },
  { label: 'Italian', value: 'italian' },
  { label: 'Mexican', value: 'mexican' },
  { label: 'Asian', value: 'asian' },
  { label: 'Pizza', value: 'pizza' },
  { label: 'Seafood', value: 'seafood' },
  { label: 'BBQ', value: 'barbecue' },
];

const HomeScreen: React.FC<HomeScreenProps> = ({ onSessionCreated, onJoinSession }) => {
  const [name, setName] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [restaurantCount, setRestaurantCount] = useState<10 | 20 | 30>(20);
  const [cuisineFilter, setCuisineFilter] = useState<CuisineFilter>('all');
  const [excludeFastFood, setExcludeFastFood] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleStart = async () => {
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!zipCode.trim() || zipCode.length < 5) { setError('Please enter a valid zip code.'); return; }

    setLoading(true);
    setError('');

    try {
      const restaurants = await fetchNearbyRestaurants(zipCode, restaurantCount, cuisineFilter, excludeFastFood);
      if (restaurants.length === 0) {
        setError('No restaurants found near that zip code. Try another.');
        setLoading(false);
        return;
      }
      const sessionCode = await createSession(name, zipCode, restaurantCount, restaurants, cuisineFilter, excludeFastFood);
      onSessionCreated(sessionCode, name, 'userA');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12 bg-dinder-blue">
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-2">🍴</div>
          <h1 className="text-5xl font-extrabold text-white mb-1">Dinder</h1>
          <p className="text-blue-100 text-sm">Swipe right on dinner.</p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Your first name"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full bg-white/20 border border-white/30 rounded-2xl px-4 py-4 text-white placeholder-white/60 text-lg focus:outline-none focus:border-white"
          />
          <input
            type="text"
            inputMode="numeric"
            placeholder="Zip code"
            value={zipCode}
            onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
            className="w-full bg-white/20 border border-white/30 rounded-2xl px-4 py-4 text-white placeholder-white/60 text-lg focus:outline-none focus:border-white"
          />

          {/* Restaurant count */}
          <div>
            <p className="text-white/70 text-sm mb-2 text-center">How many restaurants to swipe?</p>
            <div className="flex gap-3">
              {([10, 20, 30] as const).map(count => (
                <button
                  key={count}
                  onClick={() => setRestaurantCount(count)}
                  className={`flex-1 py-3 rounded-2xl font-bold text-lg transition-all ${
                    restaurantCount === count
                      ? 'bg-dinder-pink text-white'
                      : 'bg-white/20 border border-white/30 text-white/70'
                  }`}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>

          {/* Cuisine filter */}
          <div>
            <p className="text-white/70 text-sm mb-2 text-center">Cuisine</p>
            <div className="flex flex-wrap gap-2">
              {CUISINES.map(c => (
                <button
                  key={c.value}
                  onClick={() => setCuisineFilter(c.value)}
                  className={`px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                    cuisineFilter === c.value
                      ? 'bg-dinder-pink text-white'
                      : 'bg-white/20 border border-white/30 text-white/70'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Fast food toggle */}
          <div
            onClick={() => setExcludeFastFood(v => !v)}
            className="flex items-center justify-between bg-white/20 border border-white/30 rounded-2xl px-4 py-4 cursor-pointer active:scale-95 transition-all"
          >
            <div>
              <p className="text-white font-semibold">Exclude fast food</p>
              <p className="text-white/50 text-xs">Filters out major chains</p>
            </div>
            <div className={`w-12 h-7 rounded-full transition-all relative ${excludeFastFood ? 'bg-dinder-pink' : 'bg-white/30'}`}>
              <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${excludeFastFood ? 'left-6' : 'left-1'}`} />
            </div>
          </div>

          {error && <p className="text-red-200 text-sm text-center">{error}</p>}

          <button
            onClick={handleStart}
            disabled={loading}
            className="w-full bg-dinder-pink text-white font-bold text-xl py-4 rounded-2xl mt-2 disabled:opacity-50 transition-all active:scale-95"
          >
            {loading ? 'Finding restaurants...' : 'Start Session'}
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center my-6">
          <div className="flex-1 border-t border-white/30" />
          <span className="mx-4 text-white/50 text-sm">or</span>
          <div className="flex-1 border-t border-white/30" />
        </div>

        {/* Join */}
        <button
          onClick={onJoinSession}
          className="w-full border border-white/40 text-white font-semibold text-lg py-4 rounded-2xl transition-all active:scale-95"
        >
          Join a Session
        </button>

        {/* How it works */}
        <div className="mt-10 space-y-3">
          <p className="text-white/50 text-xs text-center uppercase tracking-widest mb-4">How it works</p>
          {[
            { icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
            ), text: 'Enter your zip code to find local restaurants' },
            { icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            ), text: 'Share your session code with your partner' },
            { icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
                <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
              </svg>
            ), text: "Swipe right on places you'd eat tonight" },
            { icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/>
                <line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
              </svg>
            ), text: "See your matches when you're both done" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                {item.icon}
              </div>
              <p className="text-white/70 text-sm">{item.text}</p>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default HomeScreen;